
import AreaTreeSelect from "@/components/form/AreaTreeSelect.vue";
import ElevationSelect from "@/components/form/ElevationSelect.vue";
import MapLayerItemSetup from "@/components/map-setup/MapItemSetup.vue";

// Components
import MapLayerSetup from "@/components/map-setup/MapLayerSetup.vue";
import NavHeader from "@/components/NavHeader.vue";
import MapMarkerMixin from "@/mixins/map-feature-mixins/MapMarkerMixin";
import MapInfoWindowMixin from "@/mixins/MapInfoWindowMixin";
import MapMarkerClustererMixin from "@/mixins/MapMarkerClustererMixin";
import { getMapStyle } from "@/scripts/mapping/mapStyle";

import api from "@/services/api.service";
import axios, { CancelToken, CancelTokenSource } from "axios";
import GPSService, { GeoCodeLocation } from "@/services/gps.service";
import { Group } from "@/store/areas/types";
import {
	MapBounds,
	MapData,
	MapLayer,
	MapLayerItem,
	MapLayerItemType,
	MapLayerItemTypeIds
} from "@/store/map-layers/types";
import { MapState } from "@/store/map/types";

// Types
import { FeaturesList, UserPermissions } from "@/store/types";
import { AreaNode } from "@/types/sv-data/groups/AreaNode";
// Map Utils
import {
	ClusterContextMenuResponse,
	convertStringToLatLng,
	DisplayItem,
	getEnhancedMapping,
	IEnhancedMapping,
	ILatLng,
	ILatLngBounds,
	ILayer,
	IMap,
	IMapInfoWindowService,
	IMarker,
	MapInfoWindowService,
	MapLayerItemType as MapLayerItemTypeEnum,
	MapMarkerClustererOptions,
	MapMarkerClustererService,
	MapMarkerManager,
	MarkerIcons,
	MarkerMetadata,
	MarkerMetadataRef,
	stringToBounds
} from "@sureview/v2-mapping-saas";

import { get } from "lodash";
import vSelect3 from "vselect3";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
// Third party libs
import { Component, Mixins, Ref, Vue, Watch } from "vue-property-decorator";
import { Action, Getter, Mutation, namespace, State } from "vuex-class";

// Vuex Store Modules.
const MapLayers = namespace("mapLayers");
const MapStore = namespace("map");

// Constants
const geoCoder = new GPSService();
const addressNotFoundErrorDuration: number = 6000;
const defaultLocation = { lat: 0, lng: 0 };
const roofTopZoomLevel: number = 20;
const areaZoomLevel: number = 18;
const rectangleOptions: google.maps.RectangleOptions = {
	editable: true,
	draggable: true,
	strokeColor: "#2196F3",
	fillOpacity: 0
};
@Component({
	components: {
		"area-tree-select": AreaTreeSelect,
		"map-layer-setup": MapLayerSetup,
		"map-setup-popup": MapLayerItemSetup,
		"nav-header": NavHeader,
		"vue-perfect-scrollbar": VuePerfectScrollbar,
		"v-select-3": vSelect3,
		"elevation-select": ElevationSelect
	}
})
export default class MapSetup extends Mixins(MapMarkerClustererMixin, MapMarkerMixin, MapInfoWindowMixin) {
	@Ref() mappingContainer: HTMLElement;

	@Action startApiHeartbeat: () => void;
	@State private featuresList: FeaturesList;
	@Getter("getFeature") getFeature: (featureName: string[]) => boolean;
	@Getter private getHideLabels: boolean;
	@Getter private getMapType: string;
	@Getter("getMapKey") private mapKey: string;
	@Getter private getPermissions: UserPermissions;
	@Getter private getUserGroupId: number;
	@Mutation private setMapType: (mapType: string) => void;

	@MapLayers.State("mapItemIcons") private mapIcons!: MarkerIcons;
	@MapLayers.Getter("getMapLayerItemTypes") private mapLayerItemTypes: MapLayerItemType[];
	@MapLayers.Action private loadMapLayersItemTypes: () => Promise<void>;

	@MapStore.State((state: MapState) => state.clusterContextMenuResponse)
	public readonly clusterContextMenuResponse: ClusterContextMenuResponse | null;

	private enhancedMapping: IEnhancedMapping | null = null;
	private drawingManager: google.maps.drawing.DrawingManager = null;

	// Setup Map Marker Manager
	private mapMarkerClustererService: MapMarkerClustererService | null;
	private mapMarkerClustererOptions: MapMarkerClustererOptions | null;
	private mapMarkerManager: MapMarkerManager | null;
	private mapInfoWindowService: IMapInfoWindowService | null = null;

	// Setup Context
	private mapDataInBounds: MapData = {
		items: [],
		layers: [],
		elevations: [],
		areas: []
	};
	private selectedArea: AreaNode = null;
	private selectedAreaDetails: Group = null;
	private previouslySelectedAreas: Group[] = [];
	private selectedItemType: MapLayerItemType = null;
	private selectedLayer: MapLayer = null;
	private selectedLayerFilters: MapLayer[] = [];
	private isAddingLayer: boolean = false;
	private isEditingLayer: boolean = false;
	private showMapLayerInfoBox: boolean = false;
	private isPlotting: boolean = false;
	private relocatedMapLayerItems: MapLayerItem[] = [];
	private isLoading: boolean = true;
	private isLoadingArea: boolean = true;
	private isSavingMapLayerItems: boolean = false;
	private isNoAddressErrorShown: boolean = false;
	private selectedMapLayerDimensions = null;
	private selectedFloor: number = 0;
	private groupGeoCodeLocations: Map<number, GeoCodeLocation> = new Map<number, GeoCodeLocation>();
	private selectedMapLayerItemTypes: MapLayerItemType[] = [];
	private hiddenMapLayerIds: number[] = [];
	private showFilters: boolean = false;
	private viewingGroupId: number = null;
	private mapDataCancelSource: CancelTokenSource | null;

	// Map Context
	private mapInstance: IMap | null = null;
	private mapOverlays: Map<number, ILayer> = new Map<number, ILayer>();
	private mapMarkers: Map<number, IMarker> = new Map<number, IMarker>();
	private divOverlayContainer: HTMLElement = null;
	private zoomLevel: number = 3;
	private infoWindow: google.maps.InfoWindow = null;
	private newMarker: IMarker = null;
	private previewLayerOverlay: ILayer = null;
	private rotationContainer: HTMLElement = null;
	private rotationContainerCenter: any = null;
	private rotationHandle: HTMLElement = null;
	private mapLayerPlaceholderRectangle: google.maps.Rectangle = null;
	private clusterMarkerClicked: boolean = false;

	private areaTreeSelectIsOpen: boolean = false;

	public async mounted(): Promise<void> {
		await this.onMount();
	}

	private destroyed(): void {
		this.closeMapLayerItemWindow();
		this.closeLayerSetup();
		this.clearMap();

		if(this.mapInstance) {
			this.mapInstance.reset();
		}

		if(this.enhancedMapping) {
			this.enhancedMapping.dispose();
		}

		this.mapMarkerManager.resetMarkers();
		this.mapMarkerManager = null;

		this.mapInfoWindowService?.resetInfoWindows();
		this.mapInfoWindowService = null;
	}

	private async onMount(): Promise<void> {
		if (!this.editPermission) {
			return;
		}

		await this.loadMapLayersItemTypes();
		this.startApiHeartbeat();
		await this.mountMap();

		// Will attempt to set tenant group as viewing group if permissions allow, if not, will set to the next closest group with permissions.
		this.viewingGroupId = await api.getViewingGroupId();

		let latLongForSelectedArea = await this.getLatLongForGroup(this.viewingGroupId);
		if (latLongForSelectedArea) {
			this.goToLocation(roofTopZoomLevel, latLongForSelectedArea);
		} else {
			await this.refreshMapData();
		}
	}

	private async mountMap() {
		this.enhancedMapping = await getEnhancedMapping("google", this.mapKey);
		this.mapInstance = this.enhancedMapping.createMap(
			this.mappingContainer,
			defaultLocation,
			this.zoomLevel,
			false,
			this.getMapType,
			getMapStyle(this.getMapType, this.getHideLabels),
			// Defaulted to an empty array, as CustomMapViewTypes are not currently implemented.
			[]
		);

		this.mapInstance.map.setOptions({
			zoomControl: true,
			mapTypeControl: true,
		});

		this.setupMarkerClusterer();

		if (this.isMapClusteringFeatureEnabled) {
			this.mapMarkerClustererOptions = this.markerClustererOptions;

			this.mapMarkerClustererService = new MapMarkerClustererService(
				this.enhancedMapping,
				this.mapInstance,
				this.mapMarkerClustererOptions
			);
		}

		if ( this.isMapStackedItemsFeatureEnabled || this.isMapPopOverEnabled) {
			this.mapInfoWindowService = new MapInfoWindowService(this.enhancedMapping, this.mapInstance);

			this.mapInstance!.map.addListener("zoom_changed", () => this.resetInfoWindow());
			this.mapInstance!.map.addListener("click", (event: google.maps.IconMouseEvent) => {
				if (event.placeId) {
					this.resetInfoWindow();
				}
			})
		}

		this.mapMarkerManager = new MapMarkerManager(
			this.enhancedMapping,
			this.mapInstance,
			// This flag is related to local/dev level logging, should never be true in a prod environment.
			false,
			// CanFilterByType is related to the Enterprise Unified Map Component.
			// As such it should be defaulted to false, pending further implementation.
			false,
			this.isMapSetupUseElevationLabels,
			this.mapMarkerClustererService,
			this.markerServiceOptions
		);

		// setup click handle to run when the map type changes
		this.mapInstance!.registerEvent("onMapTypeChanged", (mapType: string) => {
			this.setMapType(mapType);

			this.mapInstance!.map.setOptions({
				styles: getMapStyle(this.getMapType, this.getHideLabels)
			});
		});

		this.mapInstance!.registerEvent("onViewportChangedDelayed", this.mapViewportUpdated);
		this.mapInstance!.registerEvent("onClick", this.mapClicked);

		this.drawingManager = new google.maps.drawing.DrawingManager({
			drawingMode: google.maps.drawing.OverlayType.RECTANGLE,
			drawingControl: false,
			rectangleOptions: rectangleOptions
		});

		this.drawingManager.setMap(this.mapInstance.map);

		google.maps.event.addListener(this.drawingManager, "rectanglecomplete", this.onRectangleComplete);
		google.maps.event.addListener(this.mapInstance.map, "dragstart", this.hideRotationElements);
		google.maps.event.addListener(this.mapInstance.map, "dragend", this.setRotationElementDimensions);
		google.maps.event.addListener(this.mapInstance.map, "zoom_changed", this.hideRotationElements);
	}

	/**
	 * On Viewport Changed Event Handler
	 */
	private async mapViewportUpdated(): Promise<void> {
		if (this.mapMarkerManager.isDragging()) {
			return;
		}

		if (this.clusterMarkerClicked) {
			this.clusterMarkerClicked = false;
			return;
		}

		if (!this.isLoadingArea) {
			this.viewingGroupId = null;
		}

		this.setRotationElementDimensions();

		if (this.isEditingLayer) {
			return;
		}

		await this.refreshMapData();
	}

	/**
	 * @summary Handler for when the info window options have been altered.
	 */
	@Watch("infoWindowOptions")
	private onInfoWindowOptions(): void {
		if ((this.isMapStackedItemsFeatureEnabled || this.isMapPopOverEnabled) && this.infoWindowOptions) {
			this.mapInfoWindowService?.setInfoWindows(this.infoWindowOptions);
		}
	}

	/**
	 * @summary Handler for when the selected filters & floors have been altered.
	 */
	@Watch("selectedMapLayerItemTypes")
	@Watch("selectedLayerFilters")
	@Watch("selectedArea")
	@Watch("selectedFloor")
	private onFilterChange(): void {
		this.resetInfoWindow();
		this.relocatedMapLayerItems = [];
		this.closeMapLayerItemWindow();
	}

	@Watch("selectedFloor")
	private async refreshMapData(): Promise<void> {
		if (this.mapDataCancelSource) {
			this.mapDataCancelSource.cancel("Operation canceled due to new request.");
		}
		this.mapDataCancelSource = axios.CancelToken.source();

		let bounds = {
			north: this.mapInstance!.mapBounds.north,
			south: this.mapInstance!.mapBounds.south,
			east: this.mapInstance!.mapBounds.east,
			west: this.mapInstance!.mapBounds.west,
			elevation: this.selectedFloor
		} as MapBounds;

		let updatedMapData: MapData | null = null;
		let wasCancelled: boolean = false;
		try {
			updatedMapData = await api.getMapDataInBounds(bounds, this.mapDataCancelSource.token);
		} catch (err) {
			wasCancelled = axios.isCancel(err);
		}

		if (!wasCancelled && updatedMapData && !this.mapMarkerManager.isDragging()) {
			this.mapDataInBounds = updatedMapData;
			await this.reloadMarkers();
		}

		this.mapDataCancelSource = null;
	}

	private get editPermission(): boolean {
		return this.getPermissions.canEditMapSetup || this.getPermissions.canEditSiteSetup;
	}

	private get filteredMapData(): MapData {
		let result: MapData = {
			items: [],
			layers: [],
			elevations: [],
			areas: []
		};

		if (this.selectedArea) {
			result.items = this.mapDataInBounds.items.filter(i => i.groupId === this.selectedArea.id);
			result.layers = this.mapDataInBounds.layers.filter(l => l.groupId === this.selectedArea.id);
		} else {
			result.items = this.mapDataInBounds.items;
			result.layers = this.mapDataInBounds.layers;
		}

		if (this.selectedMapLayerItemTypes.length > 0) {
			result.items = result.items.filter(i =>
				this.selectedMapLayerItemTypes.map(t => t.mapLayerItemTypeId).includes(i.mapLayerItemTypeId)
			);
		}

		if (this.selectedLayerFilters.length > 0) {
			const selectedMapLayerFilterIds = this.selectedLayerFilters.map(l => l.mapLayerId);
			result.items = result.items.filter(i =>
				i.mapLayerIds.some(itemLayer => selectedMapLayerFilterIds.includes(itemLayer))
			);
			result.layers = result.layers.filter(l => selectedMapLayerFilterIds.includes(l.mapLayerId));
		}

		result.elevations = this.mapDataInBounds.elevations;
		return result;
	}

	private mapClicked(latLong: GeoCodeLocation) {
		if (this.isPlotting && this.selectedItemType) {
			this.onMapLayerItemPlot(latLong);
		}
	}

	/**
	 * Event handler to run on plot of a new map layer item.
	 *
	 * @param {any} latLong - Coordinates of the map layer item.
	 */
	private onMapLayerItemPlot(latLong: GeoCodeLocation) {
		let newMapLayerItemTemplate: MapLayerItem[] = [{
			mapLayerItemTypeId: this.selectedItemType.mapLayerItemTypeId,
			title: "",
			latLong: `${latLong.lat} ${latLong.lng}`,
			mapLayerItemId: 0,
			minElevation: 0,
			maxElevation: 0,
			objectId: 0,
			extraValue: null,
			regionPath: null,
			mapLayerIds: [],
			hideOnMap: false,
			groupId: null,
			lastInteractionHealthy: null,
		}];

		this.newMarker = this.handleCreateMarkers(newMapLayerItemTemplate).pop();

		this.mapInstance!.setCursor("grab");

		this.openMapLayerItemSetupModal(newMapLayerItemTemplate.pop());

		this.isPlotting = false;
	}

	/**
	 * On change of selectedArea (triggered by <area-select>), get the address for the area, and reposition the map
	 *
	 * @param {AreaNode} areaNode - The area node that has been selected
	 */
	@Watch("selectedArea")
	private async onSelectedAreaUpdated(areaNode: AreaNode) {
		if (!areaNode || areaNode.id < 0) {
			return;
		}

		await this.closeLayerSetup();
		this.isLoading = true;

		let previouslySelectedGroup = this.previouslySelectedAreas.find((g: Group) => g.groupID === areaNode.id);
		if (previouslySelectedGroup) {
			this.selectedAreaDetails = previouslySelectedGroup;
			return;
		}

		this.selectedAreaDetails = await api.getArea(areaNode.id);
		let latLongForSelectedArea = await this.getLatLongForGroup(areaNode.id);
		if (latLongForSelectedArea) {
			this.selectedAreaDetails.latLong = `${latLongForSelectedArea.lat} ${latLongForSelectedArea.lng}`;
		}

		this.previouslySelectedAreas.push(this.selectedAreaDetails);
	}

	@Watch("viewingGroupId")
	private async onViewingAreaUpdated() {
		if (this.viewingGroupId === null) {
			return;
		}

		await this.closeLayerSetup();
		this.isLoading = true;
		this.isLoadingArea = true;
		let latLongForSelectedArea = await this.getLatLongForGroup(this.viewingGroupId);
		if (latLongForSelectedArea) {
			this.goToLocation(areaZoomLevel, latLongForSelectedArea);
		} else {
			this.isLoading = false;
			this.isLoadingArea = false;
		}
	}

	private async getLatLongForGroup(groupId: number): Promise<GeoCodeLocation> {
		if (this.groupGeoCodeLocations.has(groupId)) {
			return this.groupGeoCodeLocations.get(groupId);
		}

		let result: GeoCodeLocation = null;

		let group = await api.getArea(groupId);

		// if the group already has a lat long, use that
		if (group.latLong && group.latLong.length > 0) {
			result = convertStringToLatLng(group.latLong);
		}
		// otherwise if we have an address, do a GeoLookup to get its coordinates
		else if (group && group.address && group.address.length > 0) {
			let response = await geoCoder.GeoCodeLookup(group.address, this.mapKey);
			if (response && response.results) {
				let groupLocationDetails = response.results[0];
				if (groupLocationDetails && groupLocationDetails.geometry && groupLocationDetails.geometry.location) {
					result = groupLocationDetails.geometry.location;
					await api.updateAreaLatLong(groupId, `${result.lat} ${result.lng}`);
				}
			}
			if (response && response.status == "ZERO_RESULTS") {
				this.showNoAddressError();
			}
		}

		this.groupGeoCodeLocations.set(groupId, result);

		return result;
	}

	private async goToMapLayer(mapLayer: MapLayer): Promise<void> {
		await this.closeLayerSetup();
		await this.$nextTick();
		let nwLatLng = convertStringToLatLng(mapLayer.nw);
		let seLatLng = convertStringToLatLng(mapLayer.se);

		let latCenter = (nwLatLng.lat - seLatLng.lat) / 2 + seLatLng.lat;
		let lngCenter = (seLatLng.lng - nwLatLng.lng) / 2 + nwLatLng.lng;

		this.selectedLayer = { ...mapLayer };
		this.goToLocation(roofTopZoomLevel, { lat: latCenter, lng: lngCenter });
	}

	/**
	 * Loads all available map layers and their items in the current viewport.
	 */
	@Watch("filteredMapData")
	private async displayLayersAndItems(): Promise<void> {
		if (!this.isEditingLayer) {

			// Hold a copy of the current relocated items to re-apply
			const beforeClearRelocatedMapLayerItems = [... this.relocatedMapLayerItems];

			this.clearMap();
			this.relocatedMapLayerItems = beforeClearRelocatedMapLayerItems;

			await this.displayLayers();
		}

		await this.displayItems();

		this.isLoading = false;
	}

	/**
	 * Create google map objects for each layer and adds them to the given group object
	 *
	 * @param {MapLayer[]} layers MapLayer object array
	 */
	private async displayLayers(): Promise<void> {
		for (const layer of this.filteredMapData.layers) {
			if (!this.mapOverlays.has(layer.mapLayerId) && !this.hiddenMapLayerIds.includes(layer.mapLayerId)) {
				await this.createOverlayForMapLayer(layer);
			}
		}
	}

	/**
	 * Create google map objects for each map item and adds them to the given layer object
	 */
	private displayItems(): void {
		this.handleCreateMarkers(this.filteredMapData.items)
	}

	private handleCreateMarkers(mapLayerItemList: MapLayerItem[], selectedMarkerId: string = null): IMarker[] {
		// Remove items from mapLayerItemList that match against this.relocatedMapLayerItems by mapLayerItemId
		mapLayerItemList = mapLayerItemList.filter(
			item => !this.relocatedMapLayerItems.some(relocated => relocated.mapLayerItemId === item.mapLayerItemId)
		);
		mapLayerItemList = mapLayerItemList.concat(this.relocatedMapLayerItems);

		let displayItems: DisplayItem[] = [];
		for (const mapLayerItem of mapLayerItemList) {

			if (this.mapMarkers.has(mapLayerItem.mapLayerItemId)) {
				let marker = this.mapMarkers.get(mapLayerItem.mapLayerItemId);
				marker.visible = true;
				this.mapMarkers.set(mapLayerItem.mapLayerItemId, marker);
				return [marker];
			}

			let displayItem: DisplayItem = this.createMarker({
				key: mapLayerItem.mapLayerItemId.toString(),
				typeId: mapLayerItem.mapLayerItemTypeId,
				title: mapLayerItem.title,
				position: convertStringToLatLng(mapLayerItem.latLong),
				mapLayerItemId: mapLayerItem.mapLayerItemId,
				regionPath: mapLayerItem.regionPath,
				minElevation: mapLayerItem.minElevation,
				maxElevation: mapLayerItem.maxElevation,
				objectId: mapLayerItem.objectId,
				groupId: mapLayerItem.groupId,
				state: {
					draggable: true,
					noCluster: false,
				},
			});

			if (displayItem.key == selectedMarkerId
				|| !this.isItemClusterable(displayItem.typeId)) {
				// Map Layer item types not supported for clustering
				// Additionally, the selected item to edit will not be clustered.
				displayItem.state.noCluster = true;
			}

			// Set up the click/drag handlers for the markers.
			if (displayItem.objectId) {
				displayItem.state.iconType = "active";
				displayItem.state.clickable = true;
				displayItem.state.click = () => {
					this.openMapLayerItemSetupModal(mapLayerItem);
					let clusteredMarker = this.clusterContextMenuResponse?.markers
						.find((marker: MarkerMetadata) =>
							displayItem.mapLayerItemId === marker.mapLayerItemId);

					if (clusteredMarker) {
						this.clusterMarkerClicked = true;
					}

					this.mapMarkerManager.removeMarker(displayItem.key);
					this.handleCreateMarkers(mapLayerItemList, displayItem.key);
				};

				displayItem.state.drag = async (event) => {
					let latLng: ILatLng = {
						lng: event.lng,
						lat: event.lat
					}

					this.updateMarkerItemPosition(latLng, displayItem.mapLayerItemId)
					this.refreshMapData();
				}
			}

			displayItems.push(displayItem);
		}

		let currentMarkers: IMarker[] = [];

		for (const item of this.mapMarkerManager.addMarkers(displayItems)) {
			currentMarkers.push(this.mapMarkerManager.getMarkersMap.get(item) as IMarker)
		}

		if (this.editPermission){
			currentMarkers.forEach((marker) => {
				marker.marker[MarkerMetadataRef].onClick = marker.click;
			})
		}
		return currentMarkers;
	}

	/**
	 * move to the map to the given position and zoom level
	 *
	 * @param {number} zoom - zoom level to set the map to
	 * @param {GeoCodeLocation} location - LatLong coordinates to center the map on
	 */
	private goToLocation(zoom: number = 10, location: GeoCodeLocation): void {
		if (this.mapInstance) {
			this.mapInstance.map.setZoom(zoom);
			this.mapInstance.map.setCenter(location);
		}
	}

	/**
	 * Show Address not found error
	 */
	private showNoAddressError(): void {
		this.isNoAddressErrorShown = true;
		setTimeout(() => {
			this.isNoAddressErrorShown = false;
		}, addressNotFoundErrorDuration);
	}

	/**
	 * Gets the icon for the map item type
	 *
	 * @param {MapLayerItemType} mapItemType MapItemType Object
	 * @returns {string} - Url for the image
	 */
	private getIcon(mapItemType: MapLayerItemType): string {
		// if the icon property is set, use that (custom map items)
		if (mapItemType.icon && mapItemType.icon.length > 0) {
			return `data:image/png;base64, ${mapItemType.icon}`;
		}

		if (mapItemType.mapLayerItemTypeId === MapLayerItemTypeIds.Asset) {
			return;
		}
		// otherwise return the url for the icon
		return this.mapIcons[mapItemType.mapLayerItemTypeId];
	}

	/**
	 * Event handler for updating a marker position when it is moved
	 *
	 * @param {ILatLng} latLong
	 * @param {number} mapLayerItemId
	 */
	private updateMarkerItemPosition(latLong: ILatLng, mapLayerItemId: number) {
		let mapLayerItem = this.mapDataInBounds.items.find(i => i.mapLayerItemId === mapLayerItemId);
		if (mapLayerItem) {
			let mapLayerItemCopy = { ...mapLayerItem };
			mapLayerItemCopy.latLong = `${latLong.lat} ${latLong.lng}`;

			const mapLayerItemIndex = this.relocatedMapLayerItems.findIndex(i => i.mapLayerItemId === mapLayerItemId);
			if (mapLayerItemIndex === -1) {
				this.relocatedMapLayerItems.push(mapLayerItemCopy);
			} else {
				this.relocatedMapLayerItems.splice(mapLayerItemIndex, 1, mapLayerItemCopy);
			}
		} else {
			const relocatedMapLayerItem = this.relocatedMapLayerItems.find(i => i.mapLayerItemId === mapLayerItemId);
			if (relocatedMapLayerItem) {
				relocatedMapLayerItem.latLong = `${latLong.lat} ${latLong.lng}`;
				this.mapDataInBounds.items.push(relocatedMapLayerItem);
			}
		}
	}

	/**
	 * Creates a new EnhancedMap overlay
	 *
	 * @param {MapLayer} layer
	 * @param {ILatLngBounds} bounds
	 * @param {string} image
	 */
	private async createOverlayForMapLayer(
		layer: MapLayer,
		bounds: ILatLngBounds = null,
		image: string = null
	): Promise<ILayer> {
		if (this.mapOverlays.has(layer.mapLayerId)) {
			let overlay = this.mapOverlays.get(layer.mapLayerId);
			overlay.visible = true;
			this.mapOverlays.set(layer.mapLayerId, overlay);
			return overlay;
		}

		let imageSrc = image == null ? layer.image : image;
		if (imageSrc && !imageSrc.includes("data:image")) {
			imageSrc = "data:image/png;base64, " + imageSrc;
		}

		const overlay = await this.enhancedMapping.createOverlay(
			this.mapInstance!,
			layer.mapLayerId,
			layer.title,
			bounds ?? stringToBounds(layer.ne, layer.sw),
			layer.rotation,
			imageSrc,
			layer.minElevation,
			layer.maxElevation,
			true
		);

		this.mapOverlays.set(layer.mapLayerId, overlay);

		return overlay;
	}

	/**
	 * Returns the map icon type for the given id
	 *
	 * @param {number} mapItemTypeId
	 */
	private getMapItemIcon(mapItemTypeId: number): string {
		return this.mapIcons[mapItemTypeId];
	}

	private async startAddingNewLayer(): Promise<void> {
		await this.closeLayerSetup();
		this.selectedLayer = {
			mapLayerId: 0,
			groupId: this.selectedArea ? this.selectedArea.id : null,
			image: "",
			title: "",
			sw: "",
			se: "",
			ne: "",
			nw: "",
			rotation: 0,
			minElevation: 0,
			maxElevation: 0,
			calculatedSw: "",
			calculatedSe: "",
			calculatedNe: "",
			calculatedNw: "",
		};

		// Hide other map layers as we can only get a handle on 1 at a time.
		this.hideMapLayers();
		await this.$nextTick();

		// enables drawing mode to plotting a new layer
		this.drawingManager.setMap(this.mapInstance.map);

		this.isAddingLayer = true;
		this.isEditingLayer = true;
		this.showMapLayerInfoBox = true;
	}

	/**
	 * Setups up data and sets UI to edit mode for editing the selected layer
	 *
	 * @param {MapLayer} mapLayer
	 */
	private async startEditingMapLayer(mapLayer: MapLayer): Promise<void> {
		this.resetInfoWindow();

		if (this.selectedLayer && this.selectedLayer.mapLayerId !== mapLayer.mapLayerId) {
			await this.closeLayerSetup();
			await this.goToMapLayer(mapLayer);
		}

		this.selectedLayer = mapLayer;

		// Hide other map layers as we can only get a handle on 1 at a time.
		this.hideMapLayers();

		this.isEditingLayer = true;
		this.showMapLayerInfoBox = true;

		// We must wait until state is updated otherwise the mapLayerPlaceholderRectangle wont be shown.
		await this.$nextTick();
		await this.createPreviewOverlay(this.selectedLayer);
	}

	private async onMapLayerSaved(): Promise<void> {
		await this.closeLayerSetup();
		await this.refreshMapData();
	}

	private async onMapLayerEditToggle(): Promise<void> {
		this.isEditingLayer = !this.isEditingLayer;
		if (this.isEditingLayer) {
			await this.startEditingMapLayer(this.selectedLayer);
		} else {
			await this.closeLayerSetup();
		}
	}

	/**
	 * Event handler that runs on emit from Layer Setup child component, on the upload of a layer image
	 * @param {MapLayer} mapLayer
	 */
	private async createPreviewOverlay(mapLayer: MapLayer): Promise<void> {
		if (!this.isEditingLayer && !this.isAddingLayer) {
			return;
		}

		if (!mapLayer.ne || mapLayer.ne.length <= 0) return;

		if (this.previewLayerOverlay) {
			this.previewLayerOverlay.remove();
		}

		if (this.mapLayerPlaceholderRectangle) {
			this.mapLayerPlaceholderRectangle.setMap(null);
		}

		this.previewLayerOverlay = await this.createOverlayForMapLayer(mapLayer);
		this.mapLayerPlaceholderRectangle = this.getNewGoogleMapsRectangle();
		this.mapLayerPlaceholderRectangle.setOptions(rectangleOptions);
		this.mapLayerPlaceholderRectangle.addListener("bounds_changed", this.onRectangleBoundsChanged);
		this.mapLayerPlaceholderRectangle.setBounds(this.previewLayerOverlay.bounds);
		this.mapLayerPlaceholderRectangle.setVisible(true);

		if (this.selectedMapLayerDimensions.width !== "NaN" && this.selectedMapLayerDimensions.height !== "NaN") {
			setTimeout(() => {
				this.loadRotationElement();
			}, 100);
		}
	}

	private getNewGoogleMapsRectangle(): google.maps.Rectangle {
		return new google.maps.Rectangle({ map: this.mapInstance.map });
	}

	private async updatePreviewOverlay(mapLayer: MapLayer): Promise<void> {
		if (this.mapOverlays.has(mapLayer.mapLayerId)) {
			const overlay = this.mapOverlays.get(mapLayer.mapLayerId);
			overlay.remove();
			this.mapOverlays.delete(mapLayer.mapLayerId);
		}

		this.previewLayerOverlay.remove();
		await this.createPreviewOverlay(mapLayer);
	}

	private async onLayerImageChanged(mapLayer: MapLayer) {
		this.selectedLayer = mapLayer;
		if (this.previewLayerOverlay) {
			await this.updatePreviewOverlay(mapLayer);
		} else {
			await this.createPreviewOverlay(mapLayer);
		}
	}

	private onLayerInfoChanged(mapLayer: MapLayer) {
		this.selectedLayer = mapLayer;
	}

	private async onMapLayerDeleted(mapLayerId: number): Promise<void> {
		await this.closeLayerSetup();
		if (this.mapOverlays.has(mapLayerId)) {
			const overlay = this.mapOverlays.get(mapLayerId);
			overlay.remove();
			this.mapOverlays.delete(mapLayerId);
		}
		await this.refreshMapData();
	}

	/**
	 * Clear map of all layers and items
	 */
	private clearMap(): void {
		if (this.mapInstance) {
			this.mapInstance!.setCursor("grab");
		}

		this.hideRotationElements();

		if (this.newMarker) {
			this.newMarker.remove();
		}

		if (this.drawingManager) {
			this.drawingManager.setMap(null);
		}

		if (this.mapLayerPlaceholderRectangle) {
			this.mapLayerPlaceholderRectangle.setMap(null);
		}

		if (this.previewLayerOverlay) {
			this.previewLayerOverlay.remove();
		}

		this.mapOverlays.forEach(mapOverlay => {
			mapOverlay.visible = false;
			mapOverlay.remove();
		});

		this.mapMarkers.forEach(mapMarker => {
			mapMarker.visible = false;
			mapMarker.remove();
		});

		this.mapOverlays.clear();
		this.mapMarkers.clear();

		this.relocatedMapLayerItems = [];
	}

	/**
	 * Hides the rotation UI
	 */
	private hideRotationElements() {
		if (this.rotationContainer) {
			this.rotationContainer.style.visibility = "hidden";
		}
	}

	/**
	 * Sets the dimensions and size of the rotation element for rotating a map layer image.
	 */
	private setRotationElementDimensions() {
		// find over lay div
		this.divOverlayContainer = document.getElementById("divOverlayContainer");

		if (this.divOverlayContainer && (this.isEditingLayer || this.isAddingLayer)) {
			// find rotation UI elements
			this.rotationContainer = document.getElementById("rotation-container");
			this.rotationHandle = document.getElementById("rotation-handle");
			const rect = this.divOverlayContainer.getBoundingClientRect();

			// workout top / left pixels for the rotation container
			this.rotationContainerCenter = {
				x: rect.left + rect.width / 2,
				y: rect.top + rect.height / 2
			};

			// set dimensions based on the parent attributes
			this.rotationContainer.style.top = this.rotationContainerCenter.y + "px";
			this.rotationContainer.style.left = this.rotationContainerCenter.x + "px";
			this.rotationContainer.style.width = parseInt(this.divOverlayContainer.style.width) + "px";
			this.rotationContainer.style.visibility = "visible";
		}
	}

	/**
	 * Hooks up the relevant event handlers for rotating the map layer image
	 */
	private loadRotationElement() {
		this.setRotationElementDimensions();
		// workout with transform method can be used based on what is available to the browser
		let transform = (() => {
			let prefs = ["t", "WebkitT", "MozT", "msT", "OT"],
				style = document.documentElement.style,
				p;
			for (let i = 0, len = prefs.length; i < len; i++) {
				if ((p = prefs[i] + "ransform") in style) return p;
			}

			this.$notify({
				type: "error",
				title: "Map Layer Setup issue",
				text: "This browser does not support the ability to rotate map layers"
			});
		})();

		// calculates rotation angle for the image based off where the mouse is
		let rotate = (x, y) => {
			let deltaX = x - this.rotationContainerCenter.x;
			let deltaY = y - this.rotationContainerCenter.y;

			let angle = +((Math.atan2(deltaY, deltaX) * 180) / Math.PI).toFixed();

			// store the value for our layer
			this.selectedLayer.rotation = angle;

			return angle;
		};

		// DRAGSTART
		let mousedown = event => {
			event.preventDefault();
			document.body.style.cursor = "move";
			mousemove(event);
			document.addEventListener("mousemove", mousemove);
			document.addEventListener("mouseup", mouseup);
		};

		// DRAG
		let mousemove = event => {
			this.mapLayerPlaceholderRectangle.setVisible(false);
			this.rotationContainer.style[transform] = "rotate(" + rotate(event.pageX, event.pageY) + "deg)";
			this.divOverlayContainer.style[transform] = "rotate(" + rotate(event.pageX, event.pageY) + "deg)";
		};

		// DRAGEND
		let mouseup = () => {
			this.mapLayerPlaceholderRectangle.setVisible(true);
			this.onRectangleBoundsChanged();
			document.body.style.cursor = null;
			document.removeEventListener("mouseup", mouseup);
			document.removeEventListener("mousemove", mousemove);
		};

		// DRAG START
		if (this.rotationHandle) {
			this.rotationHandle.addEventListener("mousedown", mousedown);
		}
	}

	private hideMapLayers(): void {
		if (this.mapLayerPlaceholderRectangle) {
			this.mapLayerPlaceholderRectangle.setMap(null);
		}

		if (this.previewLayerOverlay) {
			this.previewLayerOverlay.remove();
		}

		this.mapOverlays.forEach(mapOverlay => {
			mapOverlay.visible = false;
			mapOverlay.remove();
		});

		this.mapOverlays.clear();
	}

	private closeMapLayerItemWindow(): void {
		this.isPlotting = false;

		if (this.infoWindow && typeof this.infoWindow.close == "function") {
			this.infoWindow.close();
		}

		if (google.maps && google.maps.event) {
			google.maps.event.trigger(this.infoWindow, "closeclick");
		}
	}

	@Watch("markerContextMenuResponse")
    private onMarkerContextMenuResponseUpdate(): void {
		// close any open map layer item window if a marker context menu response is received
        if (this.markerContextMenuResponse) {
            this.closeMapLayerItemWindow();
        }
    }

	private async mapLayerItemDeleted(mapLayerItemId: number) {
		this.closeMapLayerItemWindow();
		this.mapMarkerManager.removeMarker(mapLayerItemId.toString());
	}

	private async mapLayerItemSaved() {
		this.closeMapLayerItemWindow();

		let bounds = {
			north: this.mapInstance!.mapBounds.north,
			south: this.mapInstance!.mapBounds.south,
			east: this.mapInstance!.mapBounds.east,
			west: this.mapInstance!.mapBounds.west,
			elevation: this.selectedFloor
		} as MapBounds;

		this.mapDataInBounds = await api.getMapDataInBounds(bounds);
		await this.reloadMarkers();
	}

	/**
	 * Opens a new instance of the Map Item Setup window
	 *
	 * @param {MapLayerItem} mapLayerItem
	 */
	private openMapLayerItemSetupModal(mapLayerItem: MapLayerItem): void {
		if (this.mapLayerPlaceholderRectangle) {
			this.mapLayerPlaceholderRectangle.setVisible(false);
		}

		this.closeMapLayerItemWindow();
		this.resetInfoWindow();

		const relocatedLatLong = this.relocatedMapLayerItems.find(item => item.mapLayerItemId == mapLayerItem.mapLayerItemId)?.latLong;
		if (relocatedLatLong) {
			mapLayerItem.latLong = relocatedLatLong
		}
		let latLong = convertStringToLatLng(mapLayerItem.latLong);
		let mapLayerItemSetupModal = Vue.extend(MapLayerItemSetup);

		const mapLayerSetupModalInstance = new mapLayerItemSetupModal({
			store: this.$store,
			propsData: {
				mapLayerItem: mapLayerItem,
				mapLayersInBounds: this.mapDataInBounds.layers,
				groupId: this.viewingGroupId,
				currentFloor: this.selectedFloor,
			}
		}).$mount();

		// hook up event handlers for emits
		mapLayerSetupModalInstance.$on("mapLayerItemSaved", () => this.mapLayerItemSaved());
		mapLayerSetupModalInstance.$on("mapLayerItemWindowClosed", () => this.closeMapLayerItemWindow());
		mapLayerSetupModalInstance.$on("mapLayerItemDeleted", (eventData: any) =>
			this.mapLayerItemDeleted(eventData.mapLayerItemId)
		);

		this.infoWindow = new google.maps.InfoWindow({
			content: mapLayerSetupModalInstance.$el,
			position: latLong,
			pixelOffset: new google.maps.Size(0, -48)
		});

		// event handler for clicking the X in the popup window
		this.infoWindow.addListener("closeclick", () => {
			if (this.mapMarkers.has(mapLayerItem.mapLayerItemId)) {
				let marker = this.mapMarkers.get(mapLayerItem.mapLayerItemId);
				marker.isDraggable = true;
				this.mapMarkers.set(mapLayerItem.mapLayerItemId, marker);
			}
			if (this.newMarker) {
				this.newMarker.remove();
			}
			if (this.mapLayerPlaceholderRectangle) {
				this.mapLayerPlaceholderRectangle.setVisible(true);
			}

			this.mapInstance.map.setOptions({gestureHandling: "greedy"});

			this.setRotationElementDimensions();
		});

		// lock the markers location while the popup window is open.
		if (this.mapMarkers.has(mapLayerItem.mapLayerItemId)) {
			let marker = this.mapMarkers.get(mapLayerItem.mapLayerItemId);
			marker.isDraggable = false;
			marker.marker.setZIndex(9999999);
			this.mapMarkers.set(mapLayerItem.mapLayerItemId, marker);
		}

		this.infoWindow.open(this.mapInstance.map);

		this.mapInstance.map.setCenter(latLong);
		this.mapInstance.map.panBy(0,-90);
		this.mapInstance.map.setOptions({gestureHandling: "none"});

		// reset the cursor for the map (changed when plotting a new item)
		this.mapInstance!.setCursor("grab");
	}

	/**
	 * Set the map to allow plotting of an item, which will trigger the "onClick" event for the map.
	 *
	 * @param {MapLayerItemType} itemType
	 */
	private beginPlottingItem(itemType: MapLayerItemType): void {
		// only allow items to be plotted if we are not currently editing or creating a layer
		if (!this.isAddingLayer) {
			this.isPlotting = true;
			this.selectedItemType = itemType;

			this.mapInstance!.setCursor("crosshair");

			if (this.mapLayerPlaceholderRectangle) {
				this.mapLayerPlaceholderRectangle.setVisible(false);
			}

			this.hideRotationElements();
		}
	}

	/**
	 * Event handler for drawing a new rectangle on the map.
	 *
	 * @param {google.maps.Rectangle} rectangle
	 */
	private onRectangleComplete(rectangle: google.maps.Rectangle): void {
		// turn off drawing mode
		this.drawingManager.setMap(null);

		// updates our rectangle object with the properties on the newly create one
		this.mapLayerPlaceholderRectangle = rectangle;

		// update the width and high display, based on the size of the rectangle
		this.updateDimensions(rectangle);

		// add an event listener to run if the size, shape or position of the rectangle is changed
		this.mapLayerPlaceholderRectangle.addListener("bounds_changed", this.onRectangleBoundsChanged);

		// create an event handler for clicking on the rectangle, this will trigger a rotation of the map layer
		google.maps.event.addListener(this.mapLayerPlaceholderRectangle, "click", () => {
			// if we have a preview layer, ie an image has been uploaded
			if (this.previewLayerOverlay) {
				// on each click, increase the rotation by 10
				this.previewLayerOverlay.rotationValue = this.previewLayerOverlay.rotationValue + 10;
				this.selectedLayer.rotation = this.previewLayerOverlay.rotationValue;
			}
		});

		// update the bounds of the selected layer
		this.setSelectedLayerBounds();
		this.createPreviewOverlay(this.selectedLayer);
	}

	/**
	 * Helper method to update the bounds of the selected layer
	 *
	 * @param {ILatLngBounds} bounds - bounds object for coordinates
	 */
	private setSelectedLayerBounds(bounds: ILatLngBounds = null): void {
		if (!bounds) {
			bounds = this.mapLayerPlaceholderRectangle.getBounds().toJSON();
		}

		this.selectedLayer.ne = `${bounds.north} ${bounds.east}`;
		this.selectedLayer.nw = `${bounds.north} ${bounds.west}`;
		this.selectedLayer.se = `${bounds.south} ${bounds.east}`;
		this.selectedLayer.sw = `${bounds.south} ${bounds.west}`;
	}

	/**
	 * Click handler that runs when the size, shape or position of the rectangle is changed
	 */
	private onRectangleBoundsChanged(): void {
		if (!this.isAddingLayer && !this.isEditingLayer) {
			return;
		}

		this.updateDimensions(this.mapLayerPlaceholderRectangle);
		this.setSelectedLayerBounds();

		if (this.previewLayerOverlay) {
			// update the position of the preview layer to match the rectangle
			this.previewLayerOverlay.bounds = this.mapLayerPlaceholderRectangle.getBounds();
			this.previewLayerOverlay.rotationValue = this.selectedLayer.rotation;
			this.setRotationElementDimensions();
		}
	}

	/**
	 * Update the calculated height and width of the rectangle
	 *
	 * @param {google.maps.Rectangle} rectangle
	 */
	private updateDimensions(rectangle: google.maps.Rectangle): void {
		let bounds = this.mapLayerPlaceholderRectangle.getBounds().toJSON();

		let height = google.maps.geometry.spherical.computeDistanceBetween(
			new google.maps.LatLng(bounds.north, bounds.east),
			new google.maps.LatLng(bounds.south, bounds.east)
		);

		let width = google.maps.geometry.spherical.computeDistanceBetween(
			new google.maps.LatLng(bounds.north, bounds.east),
			new google.maps.LatLng(bounds.north, bounds.west)
		);

		this.selectedMapLayerDimensions = {
			height: height.toFixed(2),
			width: width.toFixed(2)
		};
	}

	/**
	 * Closes the Layer Setup interface and resets objects and data
	 */
	private async closeLayerSetup(): Promise<void> {
		this.isEditingLayer = false;
		this.isAddingLayer = false;

		// remove the rectangle from the map
		if (this.mapLayerPlaceholderRectangle) {
			this.mapLayerPlaceholderRectangle.setMap(null);
		}

		// remove the preview layer from the map
		if (this.previewLayerOverlay) {
			this.previewLayerOverlay.remove();
			this.previewLayerOverlay = null;
		}

		// remove the drawing manager from the map
		if (this.drawingManager) {
			this.drawingManager.setMap(null);
		}

		if (this.rotationContainer) {
			this.rotationContainer.style.visibility = "hidden";
		}

		this.selectedLayer = null;
		this.showMapLayerInfoBox = false;
	}

	/**
	 * Updates any changes map item positions
	 */
	private saveChangesToMapLayerItems(): void {
		this.isSavingMapLayerItems = true;
		this.relocatedMapLayerItems.forEach(async mapLayerItem => {
			await api.putMapLayerItem(mapLayerItem);
			let itemToUpdate = this.mapDataInBounds.items.find(i => i.mapLayerItemId === mapLayerItem.mapLayerItemId);
			itemToUpdate.latLong = mapLayerItem.latLong;
		});

		this.relocatedMapLayerItems = [];
		setTimeout(() => {
			this.isSavingMapLayerItems = false;
		}, 200);
	}

	private cancelChangesToMapLayerItems(): void {
		this.relocatedMapLayerItems.forEach(async mapLayerItem => {
			let originalMapLayerItem = this.mapDataInBounds.items.find(i => i.mapLayerItemId === mapLayerItem.mapLayerItemId);
			if (originalMapLayerItem && this.mapMarkers.has(originalMapLayerItem.mapLayerItemId)) {
				this.mapMarkers
					.get(originalMapLayerItem.mapLayerItemId)
					.moveTo(convertStringToLatLng(originalMapLayerItem.latLong));
			}
		});
		this.relocatedMapLayerItems = [];
		this.closeMapLayerItemWindow();
		this.reloadMarkers();
	}

	/**
	 * @summary Toggle visibility of a map layer image.
	 * @param {number} mapLayerId The ID of the map layer.
	 */
	private async toggleMapLayerVisibility(mapLayerId: number): Promise<void> {
		if (this.mapOverlays.has(mapLayerId)) {
			let overlay = this.mapOverlays.get(mapLayerId);

			overlay.visible = !overlay.visible;
			this.mapOverlays.set(mapLayerId, overlay);

			if (overlay.visible) {
				this.removeMapLayerFromHidden(mapLayerId);
			}
			else if (!this.hiddenMapLayerIds.includes(mapLayerId)){
				this.hiddenMapLayerIds.push(mapLayerId);
			}
		} else {
			const mapLayerToShow = this.mapDataInBounds.layers.find(l => l.mapLayerId === mapLayerId);
			await this.createOverlayForMapLayer(mapLayerToShow);

			// If the layer was previously hidden, it should be removed from the hidden layers list:
			this.removeMapLayerFromHidden(mapLayerId);
		}
	}

	/**
	 * @summary Removes the ID of the map layer from the hidden map layer IDs array.
	 * @param {number} mapLayerId The ID of the map layer.
	 */
	private removeMapLayerFromHidden(mapLayerId: number): void {
		const indexToRemove: number = this.hiddenMapLayerIds.indexOf(mapLayerId);
		if (indexToRemove !== -1) {
			this.hiddenMapLayerIds.splice(indexToRemove, 1);
		}
	}

	/**
	 * Get text for Title tag referencing the image layer display state
	 *
	 * @param {number} mapLayerId
	 * @returns {string}
	 */
	private mapLayerImageStatusTitle(mapLayerId: number): string {
		if (!this.selectedLayer || this.selectedLayer.mapLayerId === mapLayerId) {
			return "";
		}
		return (
			(this.mapOverlays.has(mapLayerId) && this.mapOverlays.get(mapLayerId).visible ? "hide" : "show") +
			" map layer image"
		);
	}

	private getShortKey(mapItemType: MapLayerItemType): string {
		// camera
		if (mapItemType.mapLayerItemTypeId == 1) {
			return "q";
		}
		// Audio
		if (mapItemType.mapLayerItemTypeId == 2) {
			return "w";
		}
		// Door
		if (mapItemType.mapLayerItemTypeId == 3) {
			return "e";
		}
		// Alarm
		if (mapItemType.mapLayerItemTypeId == 4) {
			return "r";
		}
		// Output
		if (mapItemType.mapLayerItemTypeId == 5) {
			return "y";
		}
	}

	private get isMapSetupUseElevationLabels(): boolean {
		return get(this.featuresList, ["MapSetup", "ElevationLabels"]);
	}

	private get elevationValuesElevationsArray(): number[] {
		return this.mapDataInBounds.elevations.map( item => item.elevationValue );
	}

	private get selectedFloorForLayer() : number {
		return this.isMapSetupUseElevationLabels ? this.selectedFloor : this.selectedFloor + 1;
	}

	private async reloadMarkers(): Promise<void> {
		// Hold a copy of the current relocated items to re-apply
		const beforeClearRelocatedMapLayerItems = [... this.relocatedMapLayerItems];

		this.mapMarkerManager.resetMarkers();

		const matchingLatlong = beforeClearRelocatedMapLayerItems.filter(f => {
			const matchingItem = this.mapDataInBounds.items.find(b => b.mapLayerItemId == f.mapLayerItemId);
			return !matchingItem || matchingItem?.latLong != f.latLong;
		});

		this.relocatedMapLayerItems = matchingLatlong;

		await this.displayItems();
	}

	private resetInfoWindow(): void {
		this.resetInfoWindows(this.isMapStackedItemsFeatureEnabled, this.isMapPopOverEnabled);
	}
}
